import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import { type ReactElement } from 'react';

import {
  VIEWER_CONTROL_BG,
  VIEWER_CONTROL_BG_HOVER,
  VIEWER_CONTROL_BORDER,
  VIEWER_INK,
} from './viewerTokens';

type Props = Omit<IconButtonProps, 'color' | 'children' | 'size'> & {
  /** The accessible name. Never rendered as text — these are glyphs on media. */
  label: string;
  icon: ReactElement;
  /** 40px by default; 56 for the primary control on a phone-sized target. */
  size?: 40 | 56;
};

/**
 * A round control inside a viewer.
 *
 * `MediaIconButton` is the card version and is a white disc, because it sits
 * on a photograph inside a light page. This one is the opposite: it sits on a
 * photograph inside a dark room, so a white disc would be the brightest thing
 * on screen and pull the eye off the picture.
 *
 * It carries its own translucent disc and a hairline border rather than
 * trusting the media behind it. A white glyph straight on an unknown
 * photograph has no guaranteed contrast at all — it vanishes on a white
 * bonnet — and this is a control a visitor has to be able to find before they
 * can leave the viewer.
 *
 * No tooltip, unlike the card version: a tooltip on a touch device needs a
 * long press, and these controls are tapped.
 */
export function ViewerIconButton({ label, icon, size = 40, sx, ...rest }: Props) {
  return (
    <IconButton
      aria-label={label}
      sx={[
        {
          width: size,
          height: size,
          color: VIEWER_INK,
          backgroundColor: VIEWER_CONTROL_BG,
          border: '1px solid',
          borderColor: VIEWER_CONTROL_BORDER,
          // Legible over a bright photograph, where the disc alone is not enough.
          backdropFilter: 'blur(8px)',
          '&:hover': { backgroundColor: VIEWER_CONTROL_BG_HOVER },
          // Hover is not a thing on a touch device, so the disc must already
          // be visible — and on a desktop the focus ring has to clear the
          // dark surface rather than the page's own background.
          '&:focus-visible': {
            outline: '2px solid #FFFFFF',
            outlineOffset: 2,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {icon}
    </IconButton>
  );
}
